import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { combineLatest, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';

import { $userPreference, userPreferenceScope } from '../../../shared/constants/user-preferences';
import { IUserPreference, IUserPreferencesServerResponse } from '../../../shared/interfaces/user-preference.interface';
import { FeatureFlagService } from './feature-flag/feature-flag.service';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { UserOptinFeatures, UserOptinFeatureSettings } from '../constants/user-optin';
import { IUserOptinFeatureSettings } from '../interfaces/user-optin.interface';
import { LoadErrorHandler } from './load-error-handler';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class UserOptinService implements OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private currentOptinSettings$: ReplaySubject<IUserPreference[]> = new ReplaySubject<IUserPreference[]>(1);

  constructor(
    private userService: UserService,
    private featureFlagService: FeatureFlagService,
    private loadErrorHandler: LoadErrorHandler,
  ) {
    this.loadOptinSettings();
  }

  public hasOptedIn$(feature: UserOptinFeatures): Observable<boolean> {
    const featureSettings: IUserOptinFeatureSettings = this.getFeatureSettings(feature);

    const featureFlag$: Observable<boolean> = this.featureFlagService
      .variation$(featureSettings.featureFlag, false)
      .pipe(map(Boolean));

    const userPreference$: Observable<IUserPreference> = this.currentOptinSettings$.pipe(
      map((userPreferences: IUserPreference[]) =>
        userPreferences?.find(
          (userPreference: IUserPreference) => userPreference[$userPreference.param] === featureSettings.scopeParam,
        ),
      ),
    );

    return combineLatest([featureFlag$, userPreference$]).pipe(
      map(([featureFlagEnabled, userPreference]: [boolean, IUserPreference]): boolean => {
        if (!featureFlagEnabled) {
          return false;
        }

        return SharedCommonUtility.notNullish(userPreference)
          ? Boolean(userPreference[$userPreference.value])
          : featureSettings.defaultOptedIn;
      }),
    );
  }

  public updateOptin(feature: UserOptinFeatures, newState: boolean): void {
    const featureSettings: IUserOptinFeatureSettings = this.getFeatureSettings(feature);

    this.subscriptions.add(
      this.userService
        .updateUserPreferences(userPreferenceScope.optinSettings, {
          [featureSettings.scopeParam]: newState,
        })
        .pipe(
          take(1),
          map((response: IUserPreferencesServerResponse): IUserPreference[] => {
            return this.getOptinSettingsFromResponse(response);
          }),
          catchError((error: HttpErrorResponse): Observable<IUserPreference[]> => {
            this.handleError(error);
            return this.currentOptinSettings$.pipe(take(1));
          }),
        )
        .subscribe((updatedSettings: IUserPreference[]) => {
          this.currentOptinSettings$.next(updatedSettings);
        }),
    );
  }

  private handleError(error: HttpErrorResponse): void {
    this.loadErrorHandler.handleError('error_while_saving_user_preferences', error, UserOptinService.name);
  }

  private loadOptinSettings(): void {
    this.subscriptions.add(
      this.userService.isAuthenticated$
        .pipe(
          distinctUntilChanged(),
          switchMap((isAuthenticated: boolean): Observable<IUserPreferencesServerResponse> => {
            if (!isAuthenticated) {
              return of({ userPreferences: [], _total: 0 });
            }
            return this.userService.getUserPreferences(userPreferenceScope.optinSettings);
          }),
        )
        .subscribe((response: IUserPreferencesServerResponse) => {
          const optinPreferences: IUserPreference[] = this.getOptinSettingsFromResponse(response);
          this.currentOptinSettings$.next(optinPreferences);
        }),
    );
  }

  private getOptinSettingsFromResponse(response: IUserPreferencesServerResponse): IUserPreference[] {
    return response.userPreferences?.filter(
      (p: IUserPreference) => p[$userPreference.scope] === userPreferenceScope.optinSettings,
    );
  }

  private getFeatureSettings(feature: UserOptinFeatures): IUserOptinFeatureSettings {
    const featureSettings = UserOptinFeatureSettings.find((settings: IUserOptinFeatureSettings) => settings.feature === feature);
    if (SharedCommonUtility.isNullish(featureSettings)) {
      throw new Error(`No UserOptin feature settings for '${feature}'`);
    }
    return featureSettings;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
